<!-- 会议室管理 -->
<template>
    <div class="page">
        <div class="header">
            <div class="header-row">
                <div class="header-text">
                    <h1 class="title">会议室管理</h1>
                    <p class="subtitle">维护会议室信息、开放时段与预约规则</p>
                </div>
                <button class="add-btn" type="button" @click="onAddRoom">
                    <span class="add-icon" aria-hidden="true">+</span>
                    <span>添加会议室</span>
                </button>
            </div>
        </div>

        <div class="content">
            <div class="search-bar">
                <svg class="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M16 16l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                <input
                    v-model.trim="keyword"
                    class="search-input"
                    type="search"
                    placeholder="输入项目地/会议室名称"
                    enterkeyhint="search"
                    @keyup.enter="handleSearch"
                />
                <button class="search-btn" type="button" @click="handleSearch">搜索</button>
            </div>

            <div
                v-for="room in roomList"
                :key="room.id"
                class="room-card"
            >
                <div
                    v-if="Number(room.status) === 0"
                    class="room-status-wrap"
                >
                    <button
                        class="status-warn-btn"
                        type="button"
                        aria-label="上架提示"
                        @click.stop="onOfflineTip(room)"
                    >
                        <svg class="status-warn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M12 7v7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            <circle cx="12" cy="17.2" r="1.2" fill="currentColor"/>
                        </svg>
                    </button>
                    <span class="room-status-tag">已下架</span>
                </div>
                <div
                    class="room-main"
                    role="button"
                    tabindex="0"
                    @click="onEditRoom(room)"
                >
                    <img
                        class="room-cover"
                        :src="getRoomCover(room)"
                        :alt="room.name"
                        @error="onRoomCoverError"
                    />
                    <div class="room-body">
                        <div class="room-title-row">
                            <span class="room-name">{{ room.name }}({{ room.floor }})</span>
                        </div>
                        <div class="room-meta">{{ room.location }}</div>
                        <div class="room-meta">开放时间：{{ room.openTime }}</div>
                        <div class="room-tags">
                            <span
                                v-for="(tag, index) in room.tags"
                                :key="index"
                                class="room-tag"
                            >{{ tag }}</span>
                        </div>
                    </div>
                </div>
                <div class="room-actions">
                    <button
                        class="unlock-btn"
                        type="button"
                        @click.stop="onUnlockRoom(room)"
                    >
                        <svg class="unlock-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 11V8a5 5 0 019.9-1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <circle cx="12" cy="16" r="1.4" fill="currentColor"/>
                        </svg>
                        开锁
                    </button>
                </div>
            </div>

            <div v-if="!roomList.length" class="empty">
                {{ keyword ? '暂无相关会议室' : '暂无会议室，点击右上角添加' }}
            </div>
        </div>

        <confirm-admin-unlock
            v-model="showUnlock"
            :room-id="unlockTarget.id"
            :room-name="unlockTarget.name"
            :floor="unlockTarget.floor"
            @confirm="onUnlockSuccess"
        />
    </div>
</template>

<script>
import { getMyMeetingRoomsRequest } from '@/request/api'
import scrollKeep from '@/tools/scrollKeep'
import { isUnauthorizedError } from '@/tools/utils'
import placeholderImage from '@/assets/icon_placeholderImage.png'
import confirmAdminUnlock from './confirmAdminUnlock.vue'
import { Toast } from 'vant'

export default {
    name: 'MeetingRoomManage',
    components: {
        confirmAdminUnlock,
    },
    mixins: [scrollKeep('MeetingRoomManage')],
    data() {
        return {
            keyword: '',
            roomList: [],
            placeholderImage,
            showUnlock: false,
            unlockTarget: {
                id: '',
                name: '',
                floor: '',
            },
        }
    },
    activated() {
        this.loadRoomList()
    },
    created() {
        this.loadRoomList()
    },
    watch: {
        keyword(val, oldVal) {
            // 搜索框从有内容变为清空时，自动重新拉取完整列表
            if (!String(val || '').trim() && String(oldVal || '').trim()) {
                this.loadRoomList()
            }
        },
    },
    methods: {
        getRoomCover(room) {
            const url = room && room.cover
            return url ? url : this.placeholderImage
        },
        onRoomCoverError(event) {
            const img = event && event.target
            if (!img || img.src === this.placeholderImage) return
            img.onerror = null
            img.src = this.placeholderImage
        },
        extractResponseList(res) {
            if (Array.isArray(res)) return res
            if (res && Array.isArray(res.data)) return res.data
            if (res && res.data && Array.isArray(res.data.list)) return res.data.list
            if (res && Array.isArray(res.list)) return res.list
            return []
        },
        parseRoomEquiptTags(equipt) {
            const raw = String(equipt || '').trim()
            if (!raw) return []
            return raw
                .split(/[;；]/)
                .map((part) => part.trim())
                .filter(Boolean)
                .slice(0, 2)
        },
        resolveCoverUrl(url) {
            const IMAGE_BASE = 'https://meetingroom.shdspf.cn'
            const raw = String(url || '').trim()
            if (!raw) return ''
            if (/^https?:\/\//i.test(raw)) return raw
            if (raw.startsWith('//')) return `https:${raw}`
            const path = raw.startsWith('/') ? raw : `/${raw}`
            return `${IMAGE_BASE}${path}`
        },
        mapRoomItem(item) {
            if (!item || typeof item !== 'object') return null
            const id = item.roomId != null ? item.roomId : item.id
            const buildingName = String(item.buildingName || '').trim()
            const floor = item.floor != null && String(item.floor).trim() !== ''
                ? String(item.floor).trim()
                : ''
            const tags = []
            if (item.capacity != null && item.capacity !== '') {
                tags.push(`可容纳${item.capacity}人`)
            }
            tags.push(...this.parseRoomEquiptTags(item.roomEquipt || item.roomEquip))
            return {
                id,
                name: item.name || item.roomName || '未命名会议室',
                floor,
                location: buildingName || '—',
                openTime: item.openTime || item.openHours || '09:00-18:00',
                tags,
                cover: this.resolveCoverUrl(item.smallPic),
                status: item.status,
                source: item,
            }
        },
        loadRoomList() {
            Toast.loading({
                message: '正在加载数据',
                forbidClick: true,
                duration: 0, // 不自动关闭，需手动 clear
            });
            getMyMeetingRoomsRequest({
                searchKey: this.keyword.trim(),
            })
                .then((res) => {
                    this.roomList = this.extractResponseList(res)
                        .map((item) => this.mapRoomItem(item))
                        .filter((room) => room && room.id != null)
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.roomList = []
                    const msg = (err && (err.message || err.msg)) || '获取会议室列表失败'
                    this.$toast && this.$toast(msg)
                }).finally(() => {
                    Toast.clear();
                });
        },
        resolveCoord(item) {
            if (!item || typeof item !== 'object') return null
            const fromCoord = item.coord && typeof item.coord === 'object' ? item.coord : null
            const rawLng = item.lng != null
                ? item.lng
                : (fromCoord && fromCoord.lng != null ? fromCoord.lng : null)
            const rawLat = item.lat != null
                ? item.lat
                : (fromCoord && fromCoord.lat != null ? fromCoord.lat : null)
            const lngNum = rawLng != null && rawLng !== '' ? parseFloat(rawLng) : NaN
            const latNum = rawLat != null && rawLat !== '' ? parseFloat(rawLat) : NaN
            // 0 视为无效坐标，不传入编辑页回显
            const lng = !Number.isNaN(lngNum) && lngNum !== 0 ? lngNum : null
            const lat = !Number.isNaN(latNum) && latNum !== 0 ? latNum : null
            if (lng == null && lat == null) return null
            return { lng, lat }
        },
        buildEditPayload(item) {
            if (!item || typeof item !== 'object') return null
            const roomId = item.roomId != null ? item.roomId : item.id
            if (roomId == null) return null
            return {
                roomId,
                buildingName: String(item.buildingName || '').trim(),
                floor: item.floor != null && String(item.floor).trim() !== ''
                    ? String(item.floor).trim()
                    : '',
                name: item.name || item.roomName || '',
                roomEquipt: item.roomEquipt || item.roomEquip || '',
                deviceSn: item.deviceSn || '',
                capacity: item.capacity != null && item.capacity !== ''
                    ? String(item.capacity)
                    : '',
                pic: item.pic || item.cover || item.url || '',
                coord: this.resolveCoord(item),
                otherAdmin: String(item.otherAdmin || '').trim(),
            }
        },
        onAddRoom() {
            sessionStorage.removeItem('meetingRoomEdit')
            this.$router.push({ path: '/meetingRoomManage/add' })
        },
        onEditRoom(room) {
            if (!room || room.id == null) return
            const payload = this.buildEditPayload(room.source || room)
            if (!payload) return
            sessionStorage.setItem('meetingRoomEdit', JSON.stringify(payload))
            this.$router.push({
                path: '/meetingRoomManage/add',
                query: { id: String(room.id) },
            })
        },
        handleSearch() {
            this.loadRoomList()
        },
        onOfflineTip(room) {
            if (!room || room.id == null) return
            this.$dialog
                .confirm({
                    message: '点击开锁成功后即可上架，是否开锁？',
                })
                .then(() => {
                    this.onUnlockRoom(room)
                })
                .catch(() => {})
        },
        onUnlockRoom(room) {
            if (!room || room.id == null) return
            this.unlockTarget = {
                id: room.id,
                name: room.name,
                floor: room.floor,
            }
            this.showUnlock = true
        },
        onUnlockSuccess() {
            this.loadRoomList()
        },
    },
}
</script>

<style scoped>
.page {
    min-height: calc(100vh - 1.2rem - env(safe-area-inset-bottom));
    background: #eef3f9;
    padding-bottom: 0.2rem;
    box-sizing: border-box;
}

.header {
    background: linear-gradient(160deg, #163a68 0%, #0d2c54 100%);
    padding: 0.48rem 0.36rem 0.56rem;
    box-sizing: border-box;
}

.header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.24rem;
}

.header-text {
    flex: 1;
    min-width: 0;
}

.add-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.06rem;
    margin: 0;
    padding: 0.14rem 0.22rem;
    border: none;
    border-radius: 0.32rem;
    background: #2b7de9;
    color: #fff;
    font-size: 0.22rem;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 0.06rem 0.16rem rgba(43, 125, 233, 0.35);
}

.add-btn:active {
    opacity: 0.92;
}

.add-icon {
    font-size: 0.28rem;
    font-weight: 600;
    line-height: 1;
}

.title {
    margin: 0;
    font-size: 0.4rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.3;
}

.subtitle {
    margin: 0.16rem 0 0;
    font-size: 0.24rem;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.4;
}

.content {
    padding: 0.28rem 0.28rem 0.12rem;
    box-sizing: border-box;
}

.search-bar {
    display: flex;
    align-items: center;
    height: 0.88rem;
    padding: 0.08rem 0.08rem 0.08rem 0.28rem;
    margin-bottom: 0.28rem;
    background: #ffffff;
    border-radius: 0.2rem;
    box-shadow: 0 0.06rem 0.2rem rgba(40, 90, 160, 0.08);
    box-sizing: border-box;
}

.search-icon {
    width: 0.32rem;
    height: 0.32rem;
    flex-shrink: 0;
    color: #b0bccb;
}

.search-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    margin: 0 0.16rem;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.28rem;
    color: #1e3a5f;
}

.search-input::placeholder {
    color: #b0bccb;
}

.search-btn {
    flex-shrink: 0;
    height: 100%;
    min-width: 1.28rem;
    padding: 0 0.32rem;
    border: none;
    border-radius: 0.2rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.26rem;
    font-weight: 500;
    cursor: pointer;
}

.search-btn:active {
    opacity: 0.88;
}

.room-card {
    position: relative;
    display: flex;
    align-items: stretch;
    padding: 0.24rem 0.12rem 0.24rem 0.24rem;
    margin-bottom: 0.2rem;
    background: #ffffff;
    border-radius: 0.24rem;
    box-shadow: 0 0.08rem 0.24rem rgba(40, 90, 160, 0.06);
    box-sizing: border-box;
}

.room-main {
    flex: 1;
    min-width: 0;
    display: flex;
    cursor: pointer;
}

.room-main:active {
    opacity: 0.92;
}

.room-actions {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 0.16rem;
}

.unlock-btn {
    min-width: 1.2rem;
    height: 0.6rem;
    padding: 0 0.2rem;
    border: 1px solid #2b7de9;
    border-radius: 0.3rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.22rem;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.unlock-btn:active {
    background: #236dce;
}

.unlock-icon {
    width: 0.26rem;
    height: 0.26rem;
    margin-right: 0.06rem;
    flex-shrink: 0;
}

.room-cover {
    width: 1.72rem;
    height: 1.72rem;
    border-radius: 0.16rem;
    object-fit: cover;
    flex-shrink: 0;
    background: #d9e6f5;
}

.room-body {
    flex: 1;
    min-width: 0;
    margin-left: 0.2rem;
    display: flex;
    flex-direction: column;
}

.room-title-row {
    display: flex;
    align-items: baseline;
    min-width: 0;
    line-height: 1.3;
}

.room-name {
    flex: 1;
    min-width: 0;
    font-size: 0.3rem;
    font-weight: 500;
    color: #1e3a5f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.room-status-wrap {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.08rem;
}

.room-status-tag {
    display: inline-flex;
    align-items: center;
    padding: 0.04rem 0.16rem;
    border-top: none;
    border-right: none;
    border-radius: 0 0.24rem 0 0.16rem;
    background: #eef1f6;
    color: #8a97a8;
    font-size: 0.2rem;
    font-weight: 500;
    line-height: 1.5;
    white-space: nowrap;
}

.status-warn-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: #2b7de9;
    cursor: pointer;
    line-height: 1;
}

.status-warn-btn:active {
    opacity: 0.75;
}

.status-warn-icon {
    width: 0.28rem;
    height: 0.28rem;
    display: block;
}

.room-floor {
    flex-shrink: 0;
    font-size: 0.3rem;
    font-weight: 700;
    color: #1e3a5f;
}

.room-meta {
    margin-top: 0.06rem;
    font-size: 0.22rem;
    color: #303133;
    line-height: 1.35;
}

.room-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.1rem;
    margin-top: 0.12rem;
}

.room-tag {
    padding: 0.02rem 0.12rem;
    border: 1px solid #f0c48a;
    border-radius: 0.2rem;
    color: #d4923a;
    font-size: 0.2rem;
    line-height: 1.5;
}

.empty {
    padding: 1.2rem 0;
    text-align: center;
    font-size: 0.28rem;
    color: #9aa8bc;
}
</style>
